import { useInfiniteQuery } from '@tanstack/react-query';
import { AUDIT_ACTION_LABELS, type AuditEventsResponse } from '@k-management/shared';
import { SortHeader, useTableSort } from '../../components/SortHeader.js';
import { apiRequest } from '../../lib/api.js';
import { describeAuditDetails } from '../../lib/audit-format.js';
import { formatTimestamp } from '../../lib/format.js';
import { queryKeys } from '../../lib/query-keys.js';

/** Administrators: who changed what and when (docs/adr/ADR-007). */
export function AuditPage() {
  const events = useInfiniteQuery({
    queryKey: queryKeys.auditEvents,
    queryFn: ({ pageParam }) =>
      apiRequest<AuditEventsResponse>(
        `/audit-events?limit=50${pageParam ? `&before=${pageParam}` : ''}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextBefore,
  });

  const rows = events.data?.pages.flatMap((page) => page.events) ?? [];
  const sorting = useTableSort('audit', rows, {
    occurredAt: (event) => event.occurredAt,
    actor: (event) => event.actorEmail ?? 'sistema',
    action: (event) => AUDIT_ACTION_LABELS[event.action] ?? event.action,
    summary: (event) => event.summary,
  });

  return (
    <section className="stack">
      <div className="page-header">
        <h1>Registro attività</h1>
      </div>
      {events.isPending && <p className="muted">Caricamento…</p>}
      {events.isError && <p className="error">{events.error.message}</p>}
      {events.data && rows.length === 0 && <p className="muted">Nessuna attività registrata.</p>}
      {rows.length > 0 && (
        <div className="table-wrap card flush">
          <table className="data-table">
            <thead>
              <tr>
                <SortHeader sort={sorting} column="occurredAt">
                  Quando
                </SortHeader>
                <SortHeader sort={sorting} column="actor">
                  Chi
                </SortHeader>
                <SortHeader sort={sorting} column="action">
                  Operazione
                </SortHeader>
                <SortHeader sort={sorting} column="summary">
                  Su
                </SortHeader>
                <th>Dettagli</th>
              </tr>
            </thead>
            <tbody>
              {sorting.rows.map((event) => (
                <tr key={event.id}>
                  <td>{formatTimestamp(event.occurredAt)}</td>
                  <td>{event.actorEmail ?? 'sistema'}</td>
                  <td>{AUDIT_ACTION_LABELS[event.action] ?? event.action}</td>
                  <td>{event.summary}</td>
                  <td className="details">
                    {describeAuditDetails(event).map((line) => (
                      <span key={line}>{line}</span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {events.hasNextPage && (
        <button
          type="button"
          onClick={() => void events.fetchNextPage()}
          disabled={events.isFetchingNextPage}
        >
          {events.isFetchingNextPage ? 'Caricamento…' : 'Carica attività precedenti'}
        </button>
      )}
    </section>
  );
}
